/**
 * Admin Backend API Client
 *
 * Provides typed methods for calling admin endpoints in the Next.js API backend.
 * All authentication is handled via httpOnly cookies set by the backend.
 * Requires admin role for all operations.
 *
 * Architecture: Dashboard (Admin) → API Backend (port 3002) → Supabase
 * Endpoints: /v1/admin/* and /v1/console/*
 *
 * Features:
 * - Automatic cookie forwarding for Server Components
 * - Built-in timeout (30s) and error handling
 * - Consistent error handling via BackendApiError
 * - CID-generated TypeScript types for type safety
 * - Admin role verification on all endpoints
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { convertKeysToSnake, convertKeysToCamel } from '@/lib/utils/case-converter'

// Import CID-generated types from OpenAPI specifications
import type { components as AdminComponents } from '@/types/generated/admin'
import type { components as CreditsComponents } from '@/types/generated/credits'

const BACKEND_API_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:3002'

// ===== Types =====
// All types imported from CID-generated schemas for single source of truth

// ===== Platform Statistics =====
export type PlatformStatsResponse = AdminComponents['schemas']['PlatformStatsResponse']
export type PlatformUsageStatsResponse = AdminComponents['schemas']['PlatformUsageStatsResponse']

// ===== User Management =====
export type AdminUser = AdminComponents['schemas']['AdminUser']
export type AdminUserDetail = AdminComponents['schemas']['AdminUserDetail']
export type ListUsersResponse = AdminComponents['schemas']['ListUsersResponse']
export type AdminUserDetailResponse = AdminComponents['schemas']['AdminUserDetailResponse']
export type UpdateUserRequest = AdminComponents['schemas']['UpdateUserRequest']
export type UpdateUserResponse = AdminComponents['schemas']['UpdateUserResponse']

// ===== Organization Management =====
export type AdminOrganization = AdminComponents['schemas']['AdminOrganization']
export type AdminOrganizationDetail = AdminComponents['schemas']['AdminOrganizationDetail']
export type ListAdminOrganizationsResponse = AdminComponents['schemas']['ListAdminOrganizationsResponse']
export type AdminOrganizationDetailResponse = AdminComponents['schemas']['AdminOrganizationDetailResponse']
export type UpdateAdminOrganizationRequest = AdminComponents['schemas']['UpdateAdminOrganizationRequest']
export type UpdateAdminOrganizationResponse = AdminComponents['schemas']['UpdateAdminOrganizationResponse']

// ===== Organization Members =====
export interface AdminOrgMember {
  id: string
  userId: string
  email: string
  fullName: string | null
  role: 'owner' | 'admin' | 'member' | 'viewer'
  createdAt: string
}

// ===== Organization Teams =====
export interface AdminOrgTeam {
  id: string
  name: string
  slug: string
  description: string | null
  isActive: boolean
  memberCount: number
  createdAt: string
}

// ===== Workspace Management =====
// Note: These types are not in OpenAPI schema yet, defined manually
export interface AdminWorkspace {
  id: string
  name: string
  slug: string
  organizationId: string
  organizationName: string
  organizationSlug: string
  status: 'active' | 'archived'
  description: string | null
  environment: 'production' | 'staging' | 'development'
  createdBy: string
  createdAt: string
  updatedAt: string | null
}

export interface AdminWorkspaceDetail extends AdminWorkspace {
  organizationBillingTier: 'free' | 'pro' | 'enterprise'
  membersCount: number
  gatewayConfigKeysCount: number
  llmKeysCount: number
  totalRequests: number
  totalCost: number
}

export interface ListAdminWorkspacesResponse {
  workspaces: AdminWorkspace[]
  pagination: PaginationInfo
}

export interface AdminWorkspaceDetailResponse {
  workspace: AdminWorkspaceDetail
}

export interface UpdateAdminWorkspaceRequest {
  name?: string
  description?: string
  status?: 'active' | 'archived'
  environment?: 'production' | 'staging' | 'development'
  organizationId?: string
}

export interface UpdateAdminWorkspaceResponse {
  workspace: AdminWorkspace
}

// ===== All Organization Members =====
export interface AdminAllMember {
  id: string
  userId: string
  email: string
  fullName: string | null
  role: 'owner' | 'admin' | 'member' | 'viewer'
  organizationId: string
  organizationName: string
  organizationSlug: string
  createdAt: string
}

export interface ListAllMembersResponse {
  members: AdminAllMember[]
  pagination: PaginationInfo
}

export interface AdminAllTeamMember {
  id: string
  userId: string
  email: string
  fullName: string | null
  role: 'lead' | 'member'
  teamId: string
  teamName: string
  teamSlug: string
  organizationId: string
  organizationName: string
  createdAt: string
}

export interface AdminAllTeamMembersResponse {
  members: AdminAllTeamMember[]
  pagination: PaginationInfo
}

// ===== All Workspace Members =====
export interface AdminAllWorkspaceMember {
  id: string
  userId: string
  email: string
  fullName: string | null
  role: 'admin' | 'contributor' | 'viewer'
  workspaceId: string
  workspaceName: string
  workspaceSlug: string
  organizationId: string
  organizationName: string
  createdAt: string
}

export interface AdminAllWorkspaceMembersResponse {
  members: AdminAllWorkspaceMember[]
  pagination: PaginationInfo
}

export interface AdminWorkspaceMember {
  id: string
  userId: string
  email: string
  fullName: string | null
  role: 'admin' | 'contributor' | 'viewer'
  createdAt: string
}

// ===== Workspace Access (Organizations and Teams) =====
export interface AdminWorkspaceOrgAccess {
  id: string
  organizationId: string
  organizationName: string
  organizationSlug: string
  role: 'admin' | 'contributor' | 'viewer'
  createdAt: string
}

export interface AdminWorkspaceTeamAccess {
  id: string
  teamId: string
  teamName: string
  teamSlug: string
  organizationId: string
  organizationName: string
  role: 'admin' | 'contributor' | 'viewer'
  createdAt: string
}

// ===== Gateway Configuration (Admin) =====
export interface AdminGatewayConfig {
  id: string
  name: string
  workspaceId: string
  workspaceName: string
  workspaceSlug: string
  organizationId: string
  organizationName: string
  description: string | null
  isActive: boolean
  isDefault: boolean
  isOrphan: boolean
  keysCount: number
  totalRequests: number
  apiKeyPrefix: string | null
  createdByName: string | null
  createdByEmail: string | null
  config?: Record<string, unknown>
  createdAt: string
  updatedAt: string | null
}

export interface AdminGatewayConfigDetail extends AdminGatewayConfig {
  totalCost: number
}

export interface UpdateAdminGatewayConfigRequest {
  name?: string
  description?: string
  isActive?: boolean
  isDefault?: boolean
}

// ===== Team Management =====
// Note: These types are not in OpenAPI schema yet, defined manually
export interface AdminTeam {
  id: string
  organizationId: string
  organizationName: string
  workspaceId: string | null
  workspaceName: string | null
  costCenterId: string | null
  name: string
  description: string | null
  slug: string
  isActive: boolean
  memberCount: number
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string | null
}

export interface AdminTeamDetail extends AdminTeam {
  memberCount: number
  totalCost: number
  members?: AdminTeamMember[]
}

export interface AdminTeamMember {
  id: string
  userId: string
  email: string
  fullName: string | null
  role: 'lead' | 'member'
  createdAt: string
}

export interface ListAdminTeamsResponse {
  teams: AdminTeam[]
  pagination: PaginationInfo
}

export interface AdminTeamDetailResponse {
  team: AdminTeamDetail
}

export interface UpdateAdminTeamRequest {
  name?: string
  description?: string
  isActive?: boolean
}

export interface UpdateAdminTeamResponse {
  team: AdminTeam
}

// ===== Audit Logs =====
export type AuditLog = AdminComponents['schemas']['AuditLog']
export type AuditLogsResponse = AdminComponents['schemas']['AuditLogsResponse']

// ===== Analytics =====
export type UsageAnalyticsResponse = AdminComponents['schemas']['UsageAnalyticsResponse']
export type CostAnalyticsResponse = AdminComponents['schemas']['CostAnalyticsResponse']
export type ErrorAnalyticsResponse = AdminComponents['schemas']['ErrorAnalyticsResponse']

// ===== Admin User Management =====
export type AdminUserEntity = AdminComponents['schemas']['AdminUserEntity']
export type ListAdminsResponse = AdminComponents['schemas']['ListAdminsResponse']
export type CreateAdminRequest = AdminComponents['schemas']['CreateAdminRequest']
export type CreateAdminResponse = AdminComponents['schemas']['CreateAdminResponse']
export type AdminDetailResponse = AdminComponents['schemas']['AdminDetailResponse']
export type UpdateAdminRequest = AdminComponents['schemas']['UpdateAdminRequest']
export type UpdateAdminResponse = AdminComponents['schemas']['UpdateAdminResponse']
export type UpdateAdminPasswordRequest = AdminComponents['schemas']['UpdateAdminPasswordRequest']

// ===== Pagination =====
export type PaginationInfo = AdminComponents['schemas']['PaginationInfo']

// ===== Platform LLM Keys =====
export type PlatformLlmKey = AdminComponents['schemas']['PlatformLlmKey']
export type AdminLlmProvider = PlatformLlmKey['provider']
export type ListPlatformLlmKeysResponse = AdminComponents['schemas']['ListPlatformLlmKeysResponse']
export type CreatePlatformLlmKeyRequest = AdminComponents['schemas']['CreatePlatformLlmKeyRequest']
export type CreatePlatformLlmKeyResponse = AdminComponents['schemas']['CreatePlatformLlmKeyResponse']
export type PlatformLlmKeysStatsResponse = AdminComponents['schemas']['PlatformLlmKeysStatsResponse']

// ===== User Admin Keys (BYOK admin keys) =====
// Note: These types are not in OpenAPI schema yet, defined manually
export interface UserAdminKey {
  id: string
  llmApiKeyId: string
  provider: AdminLlmProvider
  name: string
  keyPrefix: string
  organizationId: string
  organizationName: string
  createdAt: string
  lastUsedAt: string | null
  isActive: boolean
  isAdminKey: boolean
  validationStatus?: 'pending' | 'valid' | 'invalid' | 'untested'
  validationError?: string | null
  validatedAt?: string | null
}

export interface UserAdminKeysStats {
  totalKeys: number
  activeKeys: number
  uniqueOrganizations: number
  providerDistribution: Array<{
    provider: string
    count: number
    percentage: number
  }>
}

// ===== User LLM Keys (all BYOK keys) =====
// Note: These types are not in OpenAPI schema yet, defined manually
export interface UserLlmKey {
  id: string
  llmApiKeyId: string
  provider: AdminLlmProvider
  name: string
  keyPrefix: string
  organizationId: string
  organizationName: string
  createdAt: string
  lastUsedAt: string | null
  isActive: boolean
  isAdminKey: boolean
  validationStatus?: 'pending' | 'valid' | 'invalid' | 'untested'
  validationError?: string | null
  validatedAt?: string | null
}

export interface UserLlmKeysStats {
  totalKeys: number
  activeKeys: number
  uniqueOrganizations: number
  providerDistribution: Array<{
    provider: string
    count: number
    percentage: number
  }>
}

// ===== LLM Models Management =====
export interface AdminModelProduct {
  id: string
  provider: 'openai' | 'anthropic' | 'google' | 'aws' | 'azure' | 'google_vertex' | 'deepgram' | 'elevenlabs'
  familyId: string
  displayName: string
  description?: string
  summary?: string
  releaseDate?: string
  recommended: boolean
  sortOrder: number
  isLatest: boolean
  createdAt: string
  updatedAt?: string
  mappings?: AdminModelMapping[] // Optional - only returned by get/:id, not list
  mappingsCount?: number // Returned by list endpoint
}

export interface AdminModelMapping {
  id: string
  productFamilyId: string
  modelId: string
  sortOrder: number
  createdAt: string
}

export interface AdminModelProductsResponse {
  products: AdminModelProduct[]
  pagination: {
    page: number
    pageSize: number
    totalItems: number
    totalPages: number
  }
}

export interface CreateModelProductRequest {
  provider: string
  familyId: string
  displayName: string
  description?: string
  summary?: string
  releaseDate?: string
  recommended?: boolean
  sortOrder?: number
  isLatest?: boolean
  mappings?: { modelId: string; sortOrder?: number }[]
}

export interface UpdateModelProductRequest {
  displayName?: string
  description?: string
  summary?: string
  releaseDate?: string
  recommended?: boolean
  sortOrder?: number
  isLatest?: boolean
}

export interface AdminModelStatsResponse {
  totalProducts: number
  byProvider: {
    provider: string
    count: number
  }[]
  recommendedCount: number
  totalMappings: number
}

export interface AdminModelSyncResult {
  success: boolean
  syncId: string
  status: 'completed' | 'failed'
  providersProcessed: string[]
  modelsAdded: number
  modelsUpdated: number
  modelsRemoved?: number // Only present in refresh mode
  newModels: { id: string; provider: string; displayName?: string; created?: number }[]
  errors: string[]
  startedAt: string
  completedAt: string
}

export interface AdminModelSyncHistoryResponse {
  syncHistory: {
    id: string
    syncType: 'scheduled' | 'manual' | 'webhook'
    status: 'started' | 'completed' | 'failed'
    providersSynced: string[]
    modelsAdded: number
    modelsUpdated: number
    modelsRemoved: number
    errorMessage?: string
    startedAt: string
    completedAt?: string
    triggeredBy?: string
  }[]
  count: number
}

export interface AdminDetectNewModelsResponse {
  newModels: { id: string; provider: string; displayName?: string; created?: number }[]
  count: number
  message: string
}

// ===== Database =====
export type DatabaseStatsResponse = AdminComponents['schemas']['DatabaseStatsResponse']
export type DatabaseOrganizationsResponse = AdminComponents['schemas']['DatabaseOrganizationsResponse']
export type DatabaseWorkspacesResponse = AdminComponents['schemas']['DatabaseWorkspacesResponse']

// ===== Platform Key Pools (Two-Tier Architecture) =====
// Types from platform-key-pools.yaml
import type { components as PlatformKeyPoolsComponents } from '@/types/generated/platform-key-pools'

export type PlatformKeyPool = PlatformKeyPoolsComponents['schemas']['PlatformKeyPool']
export type PlatformKeyPoolDetail = PlatformKeyPoolsComponents['schemas']['PlatformKeyPoolDetail']
export type PoolMember = PlatformKeyPoolsComponents['schemas']['PoolMember']
export type PoolHealthResponse = PlatformKeyPoolsComponents['schemas']['PoolHealthResponse']
export type GatewayPlatformKey = PlatformKeyPoolsComponents['schemas']['GatewayPlatformKey']
export type ListPlatformKeyPoolsResponse = PlatformKeyPoolsComponents['schemas']['ListPlatformKeyPoolsResponse']
export type ListPoolMembersResponse = PlatformKeyPoolsComponents['schemas']['ListPoolMembersResponse']
export type ListGatewayPlatformKeysResponse = PlatformKeyPoolsComponents['schemas']['ListGatewayPlatformKeysResponse']
export type CreatePlatformKeyPoolRequest = PlatformKeyPoolsComponents['schemas']['CreatePlatformKeyPoolRequest']
export type PlatformProvider = CreatePlatformKeyPoolRequest['provider']
export type UpdatePlatformKeyPoolRequest = PlatformKeyPoolsComponents['schemas']['UpdatePlatformKeyPoolRequest']
export type AddPoolMemberRequest = PlatformKeyPoolsComponents['schemas']['AddPoolMemberRequest']
export type UpdatePoolMemberRequest = PlatformKeyPoolsComponents['schemas']['UpdatePoolMemberRequest']
export type CreateGatewayPlatformKeyRequest = PlatformKeyPoolsComponents['schemas']['CreateGatewayPlatformKeyRequest']
export type UpdateGatewayPlatformKeyRequest = PlatformKeyPoolsComponents['schemas']['UpdateGatewayPlatformKeyRequest']
export type ProvisionPlatformKeyRequest = PlatformKeyPoolsComponents['schemas']['ProvisionPlatformKeyRequest']
export type ProvisionPlatformKeyResponse = PlatformKeyPoolsComponents['schemas']['ProvisionPlatformKeyResponse']

// ===== Orphan LLM Keys =====
export type OrphanLlmKey = {
  id: string
  provider: string
  name: string
  keyPrefix: string
  isActive: boolean
  isAdminKey: boolean
  organizationId?: string
  organizationName?: string
  createdAt: string
}

// ===== Platform Key Requests =====
export type PlatformKeyRequest = {
  id: string
  requesterUserId: string
  gatewayConfigId: string | null
  workspaceId: string
  provider: string
  status: 'pending' | 'approved' | 'rejected'
  adminNote: string | null
  resolvedBy: string | null
  resolvedAt: string | null
  createdAt: string
  updatedAt: string
  requesterEmail?: string
  workspaceName?: string
  organizationId?: string
  organizationName?: string
}

// ===== Credit Management =====
export type AdminCreditAccount = CreditsComponents['schemas']['AdminCreditAccount']
export type AdminCreditAccountListResponse = CreditsComponents['schemas']['AdminCreditAccountListResponse']
export type GrantCreditsRequest = CreditsComponents['schemas']['GrantCreditsRequest']
export type GrantCreditsResponse = CreditsComponents['schemas']['GrantCreditsResponse']
export type AdjustCreditsRequest = CreditsComponents['schemas']['AdjustCreditsRequest']
export type AdjustCreditsResponse = CreditsComponents['schemas']['AdjustCreditsResponse']
export type AdminCreditTransaction = CreditsComponents['schemas']['AdminCreditTransaction']
export type AdminCreditTransactionListResponse = CreditsComponents['schemas']['AdminCreditTransactionListResponse']
export type TransactionType = CreditsComponents['schemas']['TransactionType']

// ===== Message Response =====
export type MessageResponse = {
  message: string
}

// ===== Error Handling =====

class BackendApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message)
    this.name = 'BackendApiError'
  }
}

// ===== Axios Instance =====

/**
 * Configured Axios instance for Admin Backend API
 * - Base URL: BACKEND_API_URL
 * - Timeout: 30 seconds
 * - Credentials: Included for cookie handling
 * - Interceptors: Request/response handling + cookie forwarding
 */
export const adminAxiosInstance = axios.create({
  baseURL: BACKEND_API_URL,
  timeout: 30000, // 30 seconds
  withCredentials: true, // Send httpOnly cookies from browser
  headers: {
    'Content-Type': 'application/json',
  },
})

// ===== Request Interceptor =====

/**
 * Request interceptor:
 * - Cookies are sent automatically via withCredentials: true in browser
 * - Converts request body from camelCase to snake_case for backend API
 */
adminAxiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Convert request body from camelCase to snake_case
    if (config.data && typeof config.data === 'object') {
      config.data = convertKeysToSnake(config.data)
    }
    // Convert query params from camelCase to snake_case
    if (config.params && typeof config.params === 'object') {
      config.params = convertKeysToSnake(config.params)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ===== Response Interceptor =====

/**
 * Response interceptor:
 * - Converts response body from snake_case to camelCase for frontend use
 * - Handle errors and convert to BackendApiError
 */
adminAxiosInstance.interceptors.response.use(
  (response) => {
    // Convert response body from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      response.data = convertKeysToCamel(response.data)
    }
    return response
  },
  (error: AxiosError) => {
    // Handle errors and convert to BackendApiError
    if (error.response) {
      const status = error.response.status
      // Convert error response data from snake_case to camelCase
      const rawData: unknown = error.response.data
      const data = rawData && typeof rawData === 'object' ? convertKeysToCamel(rawData) : rawData

      // Handle 401 Unauthorized - session expired
      if (status === 401) {
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname + window.location.search
          if (currentPath !== '/admin/login') {
            window.location.href = '/admin/login'
          }
        }
      }

      throw new BackendApiError(
        data?.message || data?.error?.message || (typeof data?.error === 'string' ? data.error : null) || `HTTP ${status}`,
        status,
        data
      )
    } else if (error.request) {
      // Request made but no response (network error)
      throw new BackendApiError(
        'Network error: No response from server',
        0,
        { originalError: error.message }
      )
    } else {
      // Something else happened
      throw new BackendApiError(
        error.message || 'Unknown error',
        0,
        { originalError: error }
      )
    }
  }
)

// ===== Admin API Client =====

// Admin signin/session types (CID-generated, camelCase after dashboard codegen)
export type AdminSigninRequest = AdminComponents['schemas']['ConsoleAdminSigninRequest']
export type AdminAuthResponse = AdminComponents['schemas']['ConsoleAdminAuthResponse']
export type AdminSessionResponse = AdminComponents['schemas']['ConsoleAdminSessionResponse']

/**
 * Admin Backend API client with typed methods for admin endpoints
 *
 * All endpoints require admin role authentication.
 * Cookies are automatically detected and forwarded.
 *
 * Usage:
 * ```ts
 * import { adminBackendApi } from '@/lib/api/admin-backend'
 *
 * // Authentication
 * await adminBackendApi.auth.signin({ email: 'admin@example.com', password: 'secure123' })
 * const session = await adminBackendApi.auth.getSession()
 * await adminBackendApi.auth.signout()
 *
 * // Platform statistics
 * const stats = await adminBackendApi.stats.get()
 * const usageStats = await adminBackendApi.stats.getUsage()
 *
 * // User management
 * const users = await adminBackendApi.users.list({ page: 1 })
 * const user = await adminBackendApi.users.get(userId)
 * await adminBackendApi.users.update(userId, { fullName: 'New Name' })
 *
 * // Admin user management
 * const admins = await adminBackendApi.admins.list({ page: 1 })
 * const admin = await adminBackendApi.admins.get(adminId)
 * await adminBackendApi.admins.create({ email: 'admin@example.com', password: 'secure123', fullName: 'Admin User' })
 * await adminBackendApi.admins.update(adminId, { fullName: 'Updated Name' })
 * await adminBackendApi.admins.updatePassword(adminId, 'newPassword123')
 * await adminBackendApi.admins.delete(adminId)
 *
 * // Organization management
 * const orgs = await adminBackendApi.organizations.list()
 *
 * // Audit logs
 * const logs = await adminBackendApi.auditLogs.list({ page: 1 })
 *
 * // Analytics
 * const usageAnalytics = await adminBackendApi.analytics.getUsage({ granularity: 'day' })
 * ```
 */
export const adminBackendApi = {
  // ===== Authentication =====
  auth: {
    /**
     * Admin sign in
     * POST /v1/console/auth/signin
     */
    async signin(credentials: AdminSigninRequest): Promise<AdminAuthResponse> {
      const response = await adminAxiosInstance.post('/v1/console/auth/signin', credentials)
      return response.data
    },

    /**
     * Get current admin session
     * GET /v1/console/auth/session
     */
    async getSession(): Promise<AdminSessionResponse> {
      const response = await adminAxiosInstance.get('/v1/console/auth/session')
      return response.data
    },

    /**
     * Admin sign out
     * POST /v1/console/auth/signout
     */
    async signout(): Promise<MessageResponse> {
      const response = await adminAxiosInstance.post('/v1/console/auth/signout')
      return response.data
    },
  },
  // ===== Platform Statistics =====
  stats: {
    /**
     * Get platform statistics
     * GET /v1/admin/stats
     */
    async get(): Promise<PlatformStatsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/stats')
      return response.data
    },

    /**
     * Get platform usage statistics
     * GET /v1/admin/stats/usage
     */
    async getUsage(): Promise<PlatformUsageStatsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/stats/usage')
      return response.data
    },
  },

  // ===== User Management =====
  users: {
    /**
     * List all users
     * GET /v1/admin/users
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      role?: 'super_admin' | 'admin' | 'user'
      organizationId?: string
      hasOrganization?: boolean
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }): Promise<ListUsersResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/users', { params })
      return response.data
    },

    /**
     * Get user by ID
     * GET /v1/admin/users/:userId
     */
    async get(userId: string): Promise<AdminUserDetailResponse> {
      const response = await adminAxiosInstance.get(`/v1/admin/users/${userId}`)
      return response.data
    },

    /**
     * Update user
     * PATCH /v1/admin/users/:userId
     */
    async update(userId: string, data: UpdateUserRequest): Promise<UpdateUserResponse> {
      const response = await adminAxiosInstance.patch(`/v1/admin/users/${userId}`, data)
      return response.data
    },

    /**
     * Delete user
     * DELETE /v1/admin/users/:userId
     */
    async delete(userId: string): Promise<MessageResponse> {
      const response = await adminAxiosInstance.delete(`/v1/admin/users/${userId}`)
      return response.data
    },
  },

  // ===== Admin User Management =====
  admins: {
    /**
     * List all admin users
     * GET /v1/console/admins
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
    }): Promise<ListAdminsResponse> {
      const response = await adminAxiosInstance.get('/v1/console/admins', { params })
      return response.data
    },

    /**
     * Get admin user by ID
     * GET /v1/console/admins/:adminId
     */
    async get(adminId: string): Promise<AdminDetailResponse> {
      const response = await adminAxiosInstance.get(`/v1/console/admins/${adminId}`)
      return response.data
    },

    /**
     * Create new admin user
     * POST /v1/console/admins
     */
    async create(data: CreateAdminRequest): Promise<CreateAdminResponse> {
      const response = await adminAxiosInstance.post('/v1/console/admins', data)
      return response.data
    },

    /**
     * Update admin user
     * PATCH /v1/console/admins/:adminId
     */
    async update(adminId: string, data: UpdateAdminRequest): Promise<UpdateAdminResponse> {
      const response = await adminAxiosInstance.patch(`/v1/console/admins/${adminId}`, data)
      return response.data
    },

    /**
     * Update admin user password
     * PUT /v1/console/admins/:adminId/password
     */
    async updatePassword(adminId: string, password: string): Promise<MessageResponse> {
      const response = await adminAxiosInstance.put(`/v1/console/admins/${adminId}/password`, { newPassword: password })
      return response.data
    },

    /**
     * Delete admin user
     * DELETE /v1/console/admins/:adminId
     */
    async delete(adminId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/console/admins/${adminId}`)
    },
  },

  // ===== Organization Management =====
  organizations: {
    /**
     * List all organizations
     * GET /v1/admin/organizations
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      billingTier?: 'free' | 'pro' | 'enterprise'
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    }): Promise<ListAdminOrganizationsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/organizations', { params })
      return response.data
    },

    /**
     * Get organization by ID
     * GET /v1/admin/organizations/:id
     */
    async get(organizationId: string): Promise<AdminOrganizationDetailResponse> {
      const response = await adminAxiosInstance.get(`/v1/admin/organizations/${organizationId}`)
      return response.data
    },

    /**
     * Update organization
     * PATCH /v1/admin/organizations/:id
     */
    async update(
      organizationId: string,
      data: UpdateAdminOrganizationRequest
    ): Promise<UpdateAdminOrganizationResponse> {
      const response = await adminAxiosInstance.patch(`/v1/admin/organizations/${organizationId}`, data)
      return response.data
    },

    /**
     * Delete organization
     * DELETE /v1/admin/organizations/:id
     */
    async delete(organizationId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/organizations/${organizationId}`)
    },

    // Organization Members
    members: {
      /**
       * List organization members
       * GET /v1/admin/organizations/:orgId/members
       */
      async list(organizationId: string): Promise<{ members: AdminOrgMember[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/organizations/${organizationId}/members`)
        return response.data
      },

      /**
       * Add member to organization
       * POST /v1/admin/organizations/:orgId/members
       */
      async add(organizationId: string, data: { userId: string; role: string }): Promise<AdminOrgMember> {
        const response = await adminAxiosInstance.post(`/v1/admin/organizations/${organizationId}/members`, data)
        return response.data
      },

      /**
       * Update member role
       * PATCH /v1/admin/organizations/:orgId/members/:memberId
       */
      async update(organizationId: string, memberId: string, data: { role: string }): Promise<AdminOrgMember> {
        const response = await adminAxiosInstance.patch(
          `/v1/admin/organizations/${organizationId}/members/${memberId}`,
          data
        )
        return response.data
      },

      /**
       * Remove member from organization
       * DELETE /v1/admin/organizations/:orgId/members/:memberId
       */
      async remove(organizationId: string, memberId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/organizations/${organizationId}/members/${memberId}`)
      },
    },

    // Organization Teams
    orgTeams: {
      /**
       * List organization teams
       * GET /v1/admin/organizations/:orgId/teams
       */
      async list(organizationId: string): Promise<{ teams: AdminOrgTeam[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/organizations/${organizationId}/teams`)
        return response.data
      },

      /**
       * Add team to organization
       * POST /v1/admin/organizations/:orgId/teams
       */
      async add(organizationId: string, data: { name: string; description?: string }): Promise<AdminOrgTeam> {
        const response = await adminAxiosInstance.post(`/v1/admin/organizations/${organizationId}/teams`, data)
        return response.data
      },

      /**
       * Remove team from organization
       * DELETE /v1/admin/organizations/:orgId/teams/:teamId
       */
      async remove(organizationId: string, teamId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/organizations/${organizationId}/teams/${teamId}`)
      },
    },
  },

  // ===== Workspace Management =====
  workspaces: {
    /**
     * List all workspaces
     * GET /v1/admin/workspaces
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      organizationId?: string
      status?: 'active' | 'archived'
      environment?: 'production' | 'staging' | 'development'
    }): Promise<ListAdminWorkspacesResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/workspaces', { params })
      return response.data
    },

    /**
     * Get workspace by ID
     * GET /v1/admin/workspaces/:id
     */
    async get(workspaceId: string): Promise<AdminWorkspaceDetailResponse> {
      const response = await adminAxiosInstance.get(`/v1/admin/workspaces/${workspaceId}`)
      return response.data
    },

    /**
     * Update workspace
     * PATCH /v1/admin/workspaces/:id
     */
    async update(
      workspaceId: string,
      data: UpdateAdminWorkspaceRequest
    ): Promise<UpdateAdminWorkspaceResponse> {
      const response = await adminAxiosInstance.patch(`/v1/admin/workspaces/${workspaceId}`, data)
      return response.data
    },

    /**
     * Delete workspace
     * DELETE /v1/admin/workspaces/:id
     */
    async delete(workspaceId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/workspaces/${workspaceId}`)
    },

    // Workspace members
    members: {
      /**
       * List workspace members
       * GET /v1/admin/workspaces/:workspaceId/members
       */
      async list(workspaceId: string): Promise<{ members: AdminWorkspaceMember[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/workspaces/${workspaceId}/members`)
        return response.data
      },

      /**
       * Add member to workspace
       * POST /v1/admin/workspaces/:workspaceId/members
       */
      async add(workspaceId: string, data: { userId: string; role: 'admin' | 'contributor' | 'viewer' }): Promise<AdminWorkspaceMember> {
        const response = await adminAxiosInstance.post(`/v1/admin/workspaces/${workspaceId}/members`, data)
        return response.data
      },

      /**
       * Update workspace member role
       * PATCH /v1/admin/workspaces/:workspaceId/members/:memberId
       */
      async update(workspaceId: string, memberId: string, data: { role: 'admin' | 'contributor' | 'viewer' }): Promise<AdminWorkspaceMember> {
        const response = await adminAxiosInstance.patch(`/v1/admin/workspaces/${workspaceId}/members/${memberId}`, data)
        return response.data
      },

      /**
       * Remove member from workspace
       * DELETE /v1/admin/workspaces/:workspaceId/members/:memberId
       */
      async remove(workspaceId: string, memberId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/workspaces/${workspaceId}/members/${memberId}`)
      },
    },

    // Workspace organization access
    orgAccess: {
      /**
       * List organizations with access to workspace
       * GET /v1/admin/workspaces/:workspaceId/org-access
       */
      async list(workspaceId: string): Promise<{ access: AdminWorkspaceOrgAccess[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/workspaces/${workspaceId}/org-access`)
        return response.data
      },

      /**
       * Grant organization access to workspace
       * POST /v1/admin/workspaces/:workspaceId/org-access
       */
      async add(workspaceId: string, data: { organizationId: string; role: 'admin' | 'contributor' | 'viewer' }): Promise<AdminWorkspaceOrgAccess> {
        const response = await adminAxiosInstance.post(`/v1/admin/workspaces/${workspaceId}/org-access`, data)
        return response.data
      },

      /**
       * Update organization access role
       * PATCH /v1/admin/workspaces/:workspaceId/org-access/:accessId
       */
      async update(workspaceId: string, accessId: string, data: { role: 'admin' | 'contributor' | 'viewer' }): Promise<AdminWorkspaceOrgAccess> {
        const response = await adminAxiosInstance.patch(`/v1/admin/workspaces/${workspaceId}/org-access/${accessId}`, data)
        return response.data
      },

      /**
       * Remove organization access from workspace
       * DELETE /v1/admin/workspaces/:workspaceId/org-access/:accessId
       */
      async remove(workspaceId: string, accessId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/workspaces/${workspaceId}/org-access/${accessId}`)
      },
    },

    // Workspace team access
    teamAccess: {
      /**
       * List teams with access to workspace
       * GET /v1/admin/workspaces/:workspaceId/team-access
       */
      async list(workspaceId: string): Promise<{ access: AdminWorkspaceTeamAccess[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/workspaces/${workspaceId}/team-access`)
        return response.data
      },

      /**
       * Grant team access to workspace
       * POST /v1/admin/workspaces/:workspaceId/team-access
       */
      async add(workspaceId: string, data: { teamId: string; role: 'admin' | 'contributor' | 'viewer' }): Promise<AdminWorkspaceTeamAccess> {
        const response = await adminAxiosInstance.post(`/v1/admin/workspaces/${workspaceId}/team-access`, data)
        return response.data
      },

      /**
       * Update team access role
       * PATCH /v1/admin/workspaces/:workspaceId/team-access/:accessId
       */
      async update(workspaceId: string, accessId: string, data: { role: 'admin' | 'contributor' | 'viewer' }): Promise<AdminWorkspaceTeamAccess> {
        const response = await adminAxiosInstance.patch(`/v1/admin/workspaces/${workspaceId}/team-access/${accessId}`, data)
        return response.data
      },

      /**
       * Remove team access from workspace
       * DELETE /v1/admin/workspaces/:workspaceId/team-access/:accessId
       */
      async remove(workspaceId: string, accessId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/workspaces/${workspaceId}/team-access/${accessId}`)
      },
    },

    // Workspace gateway configurations
    gatewayConfigs: {
      /**
       * List gateway configurations for workspace
       * GET /v1/admin/workspaces/:workspaceId/gateway-configs
       */
      async list(workspaceId: string): Promise<{ gatewayConfigs: AdminGatewayConfig[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/workspaces/${workspaceId}/gateway-configs`)
        return response.data
      },

      /**
       * Get gateway configuration by ID
       * GET /v1/admin/workspaces/:workspaceId/gateway-configs/:configId
       */
      async get(workspaceId: string, configId: string): Promise<{ gatewayConfig: AdminGatewayConfigDetail }> {
        const response = await adminAxiosInstance.get(`/v1/admin/workspaces/${workspaceId}/gateway-configs/${configId}`)
        return response.data
      },

      /**
       * Create gateway configuration
       * POST /v1/admin/workspaces/:workspaceId/gateway-configs
       */
      async create(workspaceId: string, data: { name: string; description?: string; config?: Record<string, unknown> }): Promise<{ gatewayConfig: AdminGatewayConfig }> {
        const response = await adminAxiosInstance.post(`/v1/admin/workspaces/${workspaceId}/gateway-configs`, data)
        return response.data
      },

      /**
       * Delete gateway configuration
       * DELETE /v1/admin/workspaces/:workspaceId/gateway-configs/:configId
       */
      async delete(workspaceId: string, configId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/workspaces/${workspaceId}/gateway-configs/${configId}`)
      },
    },
  },

  // ===== Workspace Members (All) =====
  workspaceMembers: {
    /**
     * List all workspace members across all workspaces
     * GET /v1/admin/workspace-members
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      workspaceId?: string
      userId?: string
      organizationId?: string
      role?: 'admin' | 'contributor' | 'viewer'
    }): Promise<AdminAllWorkspaceMembersResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/workspace-members', { params })
      return response.data
    },
  },

  // ===== Team Management =====
  teams: {
    /**
     * List all teams
     * GET /v1/admin/teams
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      organizationId?: string
      workspaceId?: string
      isActive?: boolean
    }): Promise<ListAdminTeamsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/teams', { params })
      return response.data
    },

    /**
     * Get team by ID
     * GET /v1/admin/teams/:teamId
     */
    async get(teamId: string): Promise<AdminTeamDetailResponse> {
      const response = await adminAxiosInstance.get(`/v1/admin/teams/${teamId}`)
      return response.data
    },

    /**
     * Update team
     * PATCH /v1/admin/teams/:teamId
     */
    async update(teamId: string, data: UpdateAdminTeamRequest): Promise<UpdateAdminTeamResponse> {
      const response = await adminAxiosInstance.patch(`/v1/admin/teams/${teamId}`, data)
      return response.data
    },

    /**
     * Delete team
     * DELETE /v1/admin/teams/:teamId
     */
    async delete(teamId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/teams/${teamId}`)
    },

    // Team Members
    members: {
      /**
       * List team members
       * GET /v1/admin/teams/:teamId/members
       */
      async list(teamId: string): Promise<{ members: AdminTeamMember[] }> {
        const response = await adminAxiosInstance.get(`/v1/admin/teams/${teamId}/members`)
        return response.data
      },

      /**
       * Add member to team
       * POST /v1/admin/teams/:teamId/members
       */
      async add(teamId: string, data: { userId: string; role: string }): Promise<AdminTeamMember> {
        const response = await adminAxiosInstance.post(`/v1/admin/teams/${teamId}/members`, data)
        return response.data
      },

      /**
       * Update team member role
       * PATCH /v1/admin/teams/:teamId/members/:memberId
       */
      async update(teamId: string, memberId: string, data: { role: string }): Promise<AdminTeamMember> {
        const response = await adminAxiosInstance.patch(
          `/v1/admin/teams/${teamId}/members/${memberId}`,
          data
        )
        return response.data
      },

      /**
       * Remove member from team
       * DELETE /v1/admin/teams/:teamId/members/:memberId
       */
      async remove(teamId: string, memberId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/teams/${teamId}/members/${memberId}`)
      },
    },
  },

  // ===== All Organization Members =====
  members: {
    /**
     * List all organization members (across all orgs)
     * GET /v1/admin/members
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      organizationId?: string
      userId?: string
      role?: 'owner' | 'admin' | 'member' | 'viewer'
    }): Promise<ListAllMembersResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/members', { params })
      return response.data
    },
  },

  // ===== All Team Members =====
  teamMembers: {
    /**
     * List all team members (across all teams)
     * GET /v1/admin/team-members
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      teamId?: string
      userId?: string
      organizationId?: string
      role?: 'lead' | 'member'
    }): Promise<AdminAllTeamMembersResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/team-members', { params })
      return response.data
    },
  },

  // ===== Gateway Configurations =====
  gatewayConfigs: {
    /**
     * List all gateway configurations (across all workspaces)
     * GET /v1/admin/gateway-configs
     */
    async list(params?: {
      page?: number
      pageSize?: number
      search?: string
      workspaceId?: string
      organizationId?: string
      isActive?: boolean
    }): Promise<{ gatewayConfigs: AdminGatewayConfig[]; pagination: PaginationInfo }> {
      const response = await adminAxiosInstance.get('/v1/admin/gateway-configs', { params })
      return response.data
    },

    /**
     * Get gateway configuration by ID
     * GET /v1/admin/gateway-configs/:configId
     */
    async get(configId: string): Promise<AdminGatewayConfigDetail> {
      const response = await adminAxiosInstance.get(`/v1/admin/gateway-configs/${configId}`)
      return response.data
    },

    /**
     * Update gateway configuration
     * PATCH /v1/admin/gateway-configs/:configId
     */
    async update(configId: string, data: UpdateAdminGatewayConfigRequest): Promise<AdminGatewayConfigDetail> {
      const response = await adminAxiosInstance.patch(`/v1/admin/gateway-configs/${configId}`, data)
      return response.data
    },

    /**
     * Delete gateway configuration
     * DELETE /v1/admin/gateway-configs/:configId
     */
    async delete(configId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/gateway-configs/${configId}`)
    },
  },

  // ===== Audit Logs =====
  auditLogs: {
    /**
     * Get audit logs
     * GET /v1/admin/audit-logs
     */
    async list(params?: {
      page?: number
      pageSize?: number
      userId?: string
      action?: string
      resourceType?: string
      startDate?: string
      endDate?: string
    }): Promise<AuditLogsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/audit-logs', { params })
      return response.data
    },
  },

  // ===== Analytics =====
  analytics: {
    /**
     * Get usage analytics
     * GET /v1/admin/analytics/usage
     */
    async getUsage(params?: {
      startDate?: string
      endDate?: string
      granularity?: 'hour' | 'day' | 'week' | 'month'
    }): Promise<UsageAnalyticsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/analytics/usage', { params })
      return response.data
    },

    /**
     * Get cost analytics
     * GET /v1/admin/analytics/costs
     */
    async getCosts(params?: {
      startDate?: string
      endDate?: string
      groupBy?: 'provider' | 'model' | 'organization'
    }): Promise<CostAnalyticsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/analytics/costs', { params })
      return response.data
    },

    /**
     * Get error analytics
     * GET /v1/admin/analytics/errors
     */
    async getErrors(params?: {
      startDate?: string
      endDate?: string
      limit?: number
    }): Promise<ErrorAnalyticsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/analytics/errors', { params })
      return response.data
    },
  },

  // ===== Platform LLM Keys (Admin Keys for cost tracking) =====
  platformLlmKeys: {
    /**
     * List all platform LLM keys
     * GET /v1/admin/platform-llm-keys
     */
    async list(): Promise<ListPlatformLlmKeysResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/platform-llm-keys')
      return response.data
    },

    /**
     * Create platform LLM key
     * POST /v1/admin/platform-llm-keys
     */
    async create(data: CreatePlatformLlmKeyRequest): Promise<CreatePlatformLlmKeyResponse> {
      const response = await adminAxiosInstance.post('/v1/admin/platform-llm-keys', data)
      return response.data
    },

    /**
     * Update platform LLM key
     * PATCH /v1/admin/platform-llm-keys/:keyId
     */
    async update(keyId: string, data: { name?: string; isActive?: boolean }): Promise<void> {
      await adminAxiosInstance.patch(`/v1/admin/platform-llm-keys/${keyId}`, data)
    },

    /**
     * Toggle platform LLM key active status
     * PATCH /v1/admin/platform-llm-keys/:keyId/toggle
     */
    async toggleActive(keyId: string, isActive: boolean): Promise<void> {
      await adminAxiosInstance.patch(`/v1/admin/platform-llm-keys/${keyId}/toggle`, { isActive })
    },

    /**
     * Revalidate platform LLM key
     * POST /v1/admin/platform-llm-keys/:keyId/revalidate
     */
    async revalidate(keyId: string): Promise<void> {
      await adminAxiosInstance.post(`/v1/admin/platform-llm-keys/${keyId}/revalidate`)
    },

    /**
     * Delete platform LLM key
     * DELETE /v1/admin/platform-llm-keys/:keyId
     */
    async delete(keyId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/platform-llm-keys/${keyId}`)
    },

    /**
     * Get platform LLM keys statistics
     * GET /v1/admin/platform-llm-keys/stats
     */
    async getStats(params?: {
      startDate?: string
      endDate?: string
    }): Promise<PlatformLlmKeysStatsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/platform-llm-keys/stats', { params })
      return response.data
    },

    /**
     * Verify platform LLM API key before creation
     * POST /v1/admin/platform-llm-keys/verify
     */
    async verify(data: {
      provider: string
      key: string
      isAdminKey?: boolean
    }): Promise<{ valid: boolean; provider: string; message?: string; error?: string }> {
      try {
        const response = await adminAxiosInstance.post('/v1/admin/platform-llm-keys/verify', data)
        return response.data
      } catch (error: unknown) {
        // When verification fails, the API returns 400 with validation result in response body
        if (axios.isAxiosError(error) && error.response?.status === 400 && error.response.data) {
          return error.response.data
        }
        throw error
      }
    },
  },

  // ===== User Admin Keys (BYOK admin keys management) =====
  userAdminKeys: {
    /**
     * List all user admin keys (BYOK keys marked as admin)
     * GET /v1/admin/user-admin-keys
     */
    async list(): Promise<{ keys: UserAdminKey[] }> {
      const response = await adminAxiosInstance.get('/v1/admin/user-admin-keys')
      return response.data
    },

    /**
     * Get user admin keys statistics
     * GET /v1/admin/user-admin-keys/stats
     */
    async getStats(): Promise<UserAdminKeysStats> {
      const response = await adminAxiosInstance.get('/v1/admin/user-admin-keys/stats')
      return response.data
    },

    /**
     * Get a single user admin key
     * GET /v1/admin/user-admin-keys/:keyId
     */
    async get(keyId: string): Promise<{ key: UserAdminKey }> {
      const response = await adminAxiosInstance.get(`/v1/admin/user-admin-keys/${keyId}`)
      return response.data
    },

    /**
     * Update a user admin key
     * PATCH /v1/admin/user-admin-keys/:keyId
     */
    async update(keyId: string, data: { name?: string; isActive?: boolean }): Promise<void> {
      await adminAxiosInstance.patch(`/v1/admin/user-admin-keys/${keyId}`, data)
    },

    /**
     * Delete a user admin key
     * DELETE /v1/admin/user-admin-keys/:keyId
     */
    async delete(keyId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/user-admin-keys/${keyId}`)
    },

    /**
     * Revalidate a user admin key
     * POST /v1/admin/user-admin-keys/:keyId/revalidate
     */
    async revalidate(keyId: string): Promise<void> {
      await adminAxiosInstance.post(`/v1/admin/user-admin-keys/${keyId}/revalidate`)
    },

    /**
     * Toggle user admin key active status
     * PATCH /v1/admin/user-admin-keys/:keyId
     */
    async toggleActive(keyId: string, isActive: boolean): Promise<void> {
      await adminAxiosInstance.patch(`/v1/admin/user-admin-keys/${keyId}`, { isActive })
    },
  },

  // ===== User LLM Keys (BYOK keys management) =====
  userLlmKeys: {
    /**
     * List all user LLM keys (all BYOK keys)
     * GET /v1/admin/user-llm-keys
     */
    async list(): Promise<{ keys: UserLlmKey[] }> {
      const response = await adminAxiosInstance.get('/v1/admin/user-llm-keys')
      return response.data
    },

    /**
     * Get user LLM keys statistics
     * GET /v1/admin/user-llm-keys/stats
     */
    async getStats(): Promise<UserLlmKeysStats> {
      const response = await adminAxiosInstance.get('/v1/admin/user-llm-keys/stats')
      return response.data
    },

    /**
     * Get a single user LLM key
     * GET /v1/admin/user-llm-keys/:keyId
     */
    async get(keyId: string): Promise<{ key: UserLlmKey }> {
      const response = await adminAxiosInstance.get(`/v1/admin/user-llm-keys/${keyId}`)
      return response.data
    },

    /**
     * Update a user LLM key
     * PATCH /v1/admin/user-llm-keys/:keyId
     */
    async update(keyId: string, data: { name?: string; isActive?: boolean }): Promise<void> {
      await adminAxiosInstance.patch(`/v1/admin/user-llm-keys/${keyId}`, data)
    },

    /**
     * Delete a user LLM key
     * DELETE /v1/admin/user-llm-keys/:keyId
     */
    async delete(keyId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/user-llm-keys/${keyId}`)
    },

    /**
     * Revalidate a user LLM key
     * POST /v1/admin/user-llm-keys/:keyId/revalidate
     */
    async revalidate(keyId: string): Promise<void> {
      await adminAxiosInstance.post(`/v1/admin/user-llm-keys/${keyId}/revalidate`)
    },

    /**
     * Toggle user LLM key active status
     * PATCH /v1/admin/user-llm-keys/:keyId
     */
    async toggleActive(keyId: string, isActive: boolean): Promise<void> {
      await adminAxiosInstance.patch(`/v1/admin/user-llm-keys/${keyId}`, { isActive })
    },
  },

  // ===== Database =====
  database: {
    /**
     * Get database statistics
     * GET /v1/admin/database/stats
     */
    async getStats(params?: {
      organizationId?: string
      workspaceId?: string
    }): Promise<DatabaseStatsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/database/stats', { params })
      return response.data
    },

    /**
     * List organizations for database filtering
     * GET /v1/admin/database/organizations
     */
    async getOrganizations(): Promise<DatabaseOrganizationsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/database/organizations')
      return response.data
    },

    /**
     * List workspaces for an organization
     * GET /v1/admin/database/organizations/:organizationId/workspaces
     */
    async getWorkspaces(organizationId: string): Promise<DatabaseWorkspacesResponse> {
      const response = await adminAxiosInstance.get(`/v1/admin/database/organizations/${organizationId}/workspaces`)
      return response.data
    },
  },

  // ===== Platform Key Pools (Tier 2) =====
  keyPools: {
    /**
     * List all platform key pools
     * GET /v1/admin/platform-key-pools
     */
    async list(provider?: string): Promise<ListPlatformKeyPoolsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/platform-key-pools', {
        params: provider ? { provider } : undefined,
      })
      return response.data
    },

    /**
     * Get pool details with members and health
     * GET /v1/admin/platform-key-pools/:poolId
     */
    async get(poolId: string): Promise<PlatformKeyPoolDetail> {
      const response = await adminAxiosInstance.get(`/v1/admin/platform-key-pools/${poolId}`)
      return response.data
    },

    /**
     * Create a new platform key pool
     * POST /v1/admin/platform-key-pools
     */
    async create(data: CreatePlatformKeyPoolRequest): Promise<PlatformKeyPool> {
      const response = await adminAxiosInstance.post('/v1/admin/platform-key-pools', data)
      return response.data
    },

    /**
     * Update pool configuration
     * PATCH /v1/admin/platform-key-pools/:poolId
     */
    async update(poolId: string, data: UpdatePlatformKeyPoolRequest): Promise<PlatformKeyPool> {
      const response = await adminAxiosInstance.patch(`/v1/admin/platform-key-pools/${poolId}`, data)
      return response.data
    },

    /**
     * Delete a pool
     * DELETE /v1/admin/platform-key-pools/:poolId
     */
    async delete(poolId: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/platform-key-pools/${poolId}`)
    },

    /**
     * Get pool health status
     * GET /v1/admin/platform-key-pools/:poolId/health
     */
    async getHealth(poolId: string): Promise<PoolHealthResponse> {
      const response = await adminAxiosInstance.get(`/v1/admin/platform-key-pools/${poolId}/health`)
      return response.data
    },

    // Pool Members
    members: {
      /**
       * List pool members
       * GET /v1/admin/platform-key-pools/:poolId/members
       */
      async list(poolId: string): Promise<ListPoolMembersResponse> {
        const response = await adminAxiosInstance.get(`/v1/admin/platform-key-pools/${poolId}/members`)
        return response.data
      },

      /**
       * Add member to pool
       * POST /v1/admin/platform-key-pools/:poolId/members
       */
      async add(poolId: string, data: AddPoolMemberRequest): Promise<PoolMember> {
        const response = await adminAxiosInstance.post(`/v1/admin/platform-key-pools/${poolId}/members`, data)
        return response.data
      },

      /**
       * Update pool member
       * PATCH /v1/admin/platform-key-pools/:poolId/members/:memberId
       */
      async update(poolId: string, memberId: string, data: UpdatePoolMemberRequest): Promise<PoolMember> {
        const response = await adminAxiosInstance.patch(
          `/v1/admin/platform-key-pools/${poolId}/members/${memberId}`,
          data
        )
        return response.data
      },

      /**
       * Remove member from pool
       * DELETE /v1/admin/platform-key-pools/:poolId/members/:memberId
       */
      async remove(poolId: string, memberId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/platform-key-pools/${poolId}/members/${memberId}`)
      },
    },
  },

  // ===== Gateway Platform Keys (Tier 1) =====
  gatewayPlatformKeys: {
    /**
     * List all dedicated gateway platform keys
     * GET /v1/admin/gateway-platform-keys
     */
    async list(params?: { gatewayConfigId?: string; provider?: string }): Promise<ListGatewayPlatformKeysResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/gateway-platform-keys', { params })
      return response.data
    },

    /**
     * Create dedicated gateway platform key
     * POST /v1/admin/gateway-platform-keys
     */
    async create(data: CreateGatewayPlatformKeyRequest): Promise<GatewayPlatformKey> {
      const response = await adminAxiosInstance.post('/v1/admin/gateway-platform-keys', data)
      return response.data
    },

    /**
     * Update gateway platform key
     * PATCH /v1/admin/gateway-platform-keys/:id
     */
    async update(id: string, data: UpdateGatewayPlatformKeyRequest): Promise<GatewayPlatformKey> {
      const response = await adminAxiosInstance.patch(`/v1/admin/gateway-platform-keys/${id}`, data)
      return response.data
    },

    /**
     * Delete gateway platform key
     * DELETE /v1/admin/gateway-platform-keys/:id
     */
    async delete(id: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/gateway-platform-keys/${id}`)
    },

    /**
     * List orphan LLM keys (platform keys not assigned to any gateway)
     * GET /v1/admin/gateway-platform-keys/orphans
     */
    async listOrphans(): Promise<{ keys: OrphanLlmKey[] }> {
      const response = await adminAxiosInstance.get('/v1/admin/gateway-platform-keys/orphans')
      return response.data
    },
  },

  // ===== Platform Key Requests =====
  platformKeyRequests: {
    /**
     * List all platform key requests
     * GET /v1/admin/platform-key-requests
     */
    async list(params?: { status?: string }): Promise<{ requests: PlatformKeyRequest[] }> {
      const response = await adminAxiosInstance.get('/v1/admin/platform-key-requests', { params })
      return response.data
    },

    /**
     * Resolve (approve/reject) a platform key request
     * PUT /v1/admin/platform-key-requests/:id
     */
    async resolve(id: string, data: { status: 'approved' | 'rejected'; adminNote?: string }): Promise<PlatformKeyRequest> {
      const response = await adminAxiosInstance.put(`/v1/admin/platform-key-requests/${id}`, data)
      return response.data
    },
  },

  // ===== Platform Key Provisioning =====
  keyProvisioning: {
    /**
     * Auto-provision a new platform key via provider API
     * POST /v1/admin/platform-keys/provision
     */
    async provision(data: ProvisionPlatformKeyRequest): Promise<ProvisionPlatformKeyResponse> {
      const response = await adminAxiosInstance.post('/v1/admin/platform-keys/provision', data)
      return response.data
    },
  },

  // ===== Credit Management =====
  credits: {
    /**
     * List all credit accounts
     * GET /v1/admin/credits/accounts
     */
    async listAccounts(params?: {
      limit?: number
      offset?: number
      sortBy?: 'balance' | 'lifetime_usage' | 'created_at'
      sortOrder?: 'asc' | 'desc'
      search?: string
    }): Promise<AdminCreditAccountListResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/credits/accounts', { params })
      return response.data
    },

    /**
     * Grant credits to an organization
     * POST /v1/admin/credits/grant
     */
    async grant(data: GrantCreditsRequest): Promise<GrantCreditsResponse> {
      const response = await adminAxiosInstance.post('/v1/admin/credits/grant', data)
      return response.data
    },

    /**
     * Adjust credits for an organization (add or remove)
     * POST /v1/admin/credits/adjust
     */
    async adjust(data: AdjustCreditsRequest): Promise<AdjustCreditsResponse> {
      const response = await adminAxiosInstance.post('/v1/admin/credits/adjust', data)
      return response.data
    },

    /**
     * List all credit transactions across all organizations
     * GET /v1/admin/credits/transactions
     */
    async listTransactions(params?: {
      limit?: number
      offset?: number
      organizationId?: string
      transactionType?: TransactionType
      startDate?: string
      endDate?: string
      sortOrder?: 'asc' | 'desc'
    }): Promise<AdminCreditTransactionListResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/credits/transactions', { params })
      return response.data
    },
  },

  // ===== LLM Models Management =====
  models: {
    /**
     * List all model products
     * GET /v1/admin/models
     */
    async list(params?: {
      provider?: string
      recommended?: boolean
      search?: string
      page?: number
      pageSize?: number
    }): Promise<AdminModelProductsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/models', { params })
      return response.data
    },

    /**
     * Get model product by ID
     * GET /v1/admin/models/:id
     */
    async get(id: string): Promise<AdminModelProduct> {
      const response = await adminAxiosInstance.get(`/v1/admin/models/${id}`)
      return response.data
    },

    /**
     * Create a new model product
     * POST /v1/admin/models
     */
    async create(data: CreateModelProductRequest): Promise<AdminModelProduct> {
      const response = await adminAxiosInstance.post('/v1/admin/models', data)
      return response.data
    },

    /**
     * Update a model product
     * PATCH /v1/admin/models/:id
     */
    async update(id: string, data: UpdateModelProductRequest): Promise<AdminModelProduct> {
      const response = await adminAxiosInstance.patch(`/v1/admin/models/${id}`, data)
      return response.data
    },

    /**
     * Delete a model product
     * DELETE /v1/admin/models/:id
     */
    async delete(id: string): Promise<void> {
      await adminAxiosInstance.delete(`/v1/admin/models/${id}`)
    },

    /**
     * Toggle recommended status
     * POST /v1/admin/models/:id/toggle-recommended
     */
    async toggleRecommended(id: string): Promise<AdminModelProduct> {
      const response = await adminAxiosInstance.post(`/v1/admin/models/${id}/toggle-recommended`)
      return response.data
    },

    /**
     * Get model statistics
     * GET /v1/admin/models/stats
     */
    async getStats(): Promise<AdminModelStatsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/models/stats')
      return response.data
    },

    /**
     * Trigger model sync
     * POST /v1/admin/models/sync
     */
    async sync(data?: { providers?: string[]; dryRun?: boolean; enrichWithAI?: boolean; refresh?: boolean }): Promise<AdminModelSyncResult> {
      const response = await adminAxiosInstance.post('/v1/admin/models/sync', data || {})
      return response.data
    },

    /**
     * Get sync history
     * GET /v1/admin/models/sync
     */
    async getSyncHistory(limit?: number): Promise<AdminModelSyncHistoryResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/models/sync', { params: { limit } })
      return response.data
    },

    /**
     * Detect new models (dry run)
     * GET /v1/admin/models/sync/detect
     */
    async detectNewModels(providers?: string): Promise<AdminDetectNewModelsResponse> {
      const response = await adminAxiosInstance.get('/v1/admin/models/sync/detect', {
        params: providers ? { providers } : undefined,
      })
      return response.data
    },

    // Mappings
    mappings: {
      /**
       * Add a mapping to a model product
       * POST /v1/admin/models/:id/mappings
       */
      async add(productId: string, data: { modelId: string; sortOrder?: number }): Promise<AdminModelMapping> {
        const response = await adminAxiosInstance.post(`/v1/admin/models/${productId}/mappings`, data)
        return response.data
      },

      /**
       * Update a mapping
       * PATCH /v1/admin/models/:id/mappings/:mappingId
       */
      async update(
        productId: string,
        mappingId: string,
        data: { modelId?: string; sortOrder?: number }
      ): Promise<AdminModelMapping> {
        const response = await adminAxiosInstance.patch(`/v1/admin/models/${productId}/mappings/${mappingId}`, data)
        return response.data
      },

      /**
       * Delete a mapping
       * DELETE /v1/admin/models/:id/mappings/:mappingId
       */
      async delete(productId: string, mappingId: string): Promise<void> {
        await adminAxiosInstance.delete(`/v1/admin/models/${productId}/mappings/${mappingId}`)
      },
    },
  },
}

// Export error class for external use
export { BackendApiError }
